import { MessageBarType } from '@fluentui/react';
import { useToast } from '@h2oai/ui-kit';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { getQueryOptionsWorkspace } from '../../authz/authz.hooks';
import { Workspace, workspaceName } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { workspaceIdFromName } from '../../authz/utils';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import { LocalStorageKeys, useCloudPlatformDiscovery, useLocalStorage, useUser } from '../../utils/hooks';
import { stripResourceDetailFromPath } from '../../utils/utils';
import { RoutePaths } from '../RoutePaths';
import { useMutateWorkspace, useWorkspaceQueries } from './hooks';
import { WorkspaceMutations } from './hooks/useMutateWorkspace';

type WorkspaceContextType = WorkspaceMutations & {
  activeWorkspace?: Workspace;
  ACTIVE_WORKSPACE_NAME?: string;
  ACTIVE_WORKSPACE_ID?: string;
  activeWorkspaceInitialized: boolean;
  activateWorkspace: (name: string) => void;
  defaultWorkspace?: Workspace;
  initializationError?: string;
};

type SwitcherProps = {
  currentWorkspace: string;
  destinationWorkspace: string;
  activateDestinationWorkspace: () => void;
};

const WorkspaceContext = React.createContext<WorkspaceContextType | undefined>(undefined);

const APPS_SUPPORTING_WORKSPACE_SWITCHER = [RoutePaths.ORCHESTRATOR_ENTRY, RoutePaths.MLOPS_ENTRY] as const;

const WorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const user = useUser();
  const history = useHistory();
  const { addToast } = useToast();
  const { pathname } = location;
  const skipProceedDialogRef = React.useRef(false);

  const [storedWorkspace, setStoredWorkspace] = useLocalStorage<Workspace | undefined>(
    LocalStorageKeys.ACTIVE_WORKSPACE
  );
  const activeWorkspaceId = React.useMemo<undefined | string>(
    () => workspaceIdFromName(storedWorkspace?.name),
    [storedWorkspace?.name]
  );

  const queries = useWorkspaceQueries({
    user,
    activeWorkspaceId,
    pathname,
  });

  const {
    activeWorkspace,
    activeWorkspaceValid,
    activeWorkspaceInitialized,
    initializationError,
    defaultWorkspace,
    workspaceFromURL,
    workspaceFromURLFetched,
    workspaceNotFound,
    workspaceIdFromURL,
  } = queries;

  const [workspaceSwitcherProps, setWorkspaceSwitcherProps] = React.useState<SwitcherProps>();

  const handlePathNavigation = React.useCallback(
    (workspaceToActivate: string) => {
      for (const appEntry of APPS_SUPPORTING_WORKSPACE_SWITCHER) {
        const workspacesPrefix = `${appEntry}/workspaces/`;
        if (pathname.startsWith(workspacesPrefix) && !pathname.endsWith('workspaces/')) {
          const pathnameEnd = pathname.replace(workspacesPrefix, '').split(/\/(.*)/s)[1];
          const pathToNavigateTo = `${appEntry}/${workspaceToActivate}${pathnameEnd ? `/${pathnameEnd}` : ''}`;
          history.push(stripResourceDetailFromPath(pathToNavigateTo));
          break;
        }
      }
    },
    [pathname, history]
  );

  const updatePreferences = React.useCallback(
    (workspaceToActivate: Workspace) => {
      setStoredWorkspace(workspaceToActivate);
      handlePathNavigation(workspaceToActivate.name!);
      skipProceedDialogRef.current = false;
    },
    [setStoredWorkspace, handlePathNavigation]
  );

  const workspaceMutations = useMutateWorkspace(updatePreferences);

  const queryClient = useQueryClient();
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';

  const activateWorkspace = React.useCallback(
    async (workspaceToActivate: string) => {
      if (workspaceToActivate === activeWorkspace?.name) return;
      try {
        const { workspace: workspaceId } = workspaceName.parse(workspaceToActivate);
        const workspace = await queryClient.fetchQuery(
          getQueryOptionsWorkspace(authzUrl || ``, workspaceId, queryClient)
        );
        skipProceedDialogRef.current = true;
        updatePreferences(workspace);
      } catch {
        const message = `Workspace you are trying to activate was not found.`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      }
    },
    [activeWorkspace?.name, updatePreferences, queryClient, authzUrl, addToast]
  );

  React.useEffect(() => {
    if (activeWorkspaceInitialized || (activeWorkspace && activeWorkspaceValid === undefined)) {
      return;
    }

    if (workspaceIdFromURL) {
      if (!workspaceFromURLFetched) return;
      if (workspaceFromURL) {
        void activateWorkspace(workspaceFromURL.name!);
        return;
      }
    }

    if (defaultWorkspace) {
      void activateWorkspace(defaultWorkspace.name!);
    }
  }, [
    activeWorkspaceInitialized,
    activeWorkspaceValid,
    defaultWorkspace?.name,
    workspaceFromURL?.name,
    workspaceFromURLFetched,
    workspaceIdFromURL,
    activateWorkspace,
  ]);

  React.useEffect(() => {
    if (!activeWorkspace || !activeWorkspaceValid) return;
    if (workspaceFromURL && workspaceFromURL.name !== activeWorkspace.name && !skipProceedDialogRef.current) {
      setWorkspaceSwitcherProps({
        currentWorkspace: activeWorkspace.displayName || '',
        destinationWorkspace: workspaceFromURL.displayName || '',
        activateDestinationWorkspace: () => {
          void activateWorkspace(workspaceFromURL.name!);
          setWorkspaceSwitcherProps(undefined);
        },
      });
    }
  }, [activeWorkspace, activeWorkspaceValid, workspaceFromURL, activateWorkspace]);

  const value = {
    ACTIVE_WORKSPACE_ID: activeWorkspaceId,
    ACTIVE_WORKSPACE_NAME: activeWorkspace?.name,
    activateWorkspace,
    activeWorkspace,
    activeWorkspaceInitialized,
    defaultWorkspace,
    initializationError,
    ...workspaceMutations,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {workspaceSwitcherProps ? (
        <NoItemView
          actionIcon="NavigateForward"
          title={`The URL is not from your active workspace`}
          description={`Do you want to leave ${workspaceSwitcherProps.currentWorkspace} and activate ${workspaceSwitcherProps.destinationWorkspace}?`}
          actionTitle={`Proceed to ${workspaceSwitcherProps.destinationWorkspace}`}
          onActionClick={workspaceSwitcherProps.activateDestinationWorkspace}
        />
      ) : workspaceNotFound ? (
        <NoItemView
          actionIcon="NavigateForward"
          title="Workspace not found"
          description="The workspace you are trying to access does not exist or you do not have access to it."
          actionTitle="Go to workspace selection"
          onActionClick={() => {
            history.push('/manage-workspaces');
          }}
        />
      ) : (
        children
      )}
    </WorkspaceContext.Provider>
  );
};

const useWorkspaces = (): WorkspaceContextType => {
  const context = React.useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspaces must be used within a WorkspaceProvider');
  }
  return context;
};

export { useWorkspaces, WorkspaceProvider };
